import { useMemo } from 'react';
import { TopBarMixedGroup } from '@components/features/filters/partials/TopBarMixedGroup';
import { ConfineResultsForm } from '@components/features/filters/forms';
import { CustomPillTopBarProps } from '@type/filters';
import { filterStyles } from '@components/features/filters/helpers/filterStyles';


/**
 * @description The top bar Confine Results filter component.
 *
 * - **submitFunc** - the function to trigger the form submit.
 * - **recordFunc** - the 'record' function from useBjForm.
 * - **clearGroup** - the function to clear the group. Defaults to undefined.
 * - **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 * - **queryParams** - the query params.
 * - **updateFormElements** - the function to force the update the form elements on open. Defaults to undefined.
 * - **updateFormValues** - the function to force the update the form values on open. Defaults to undefined.
 * - **filtersLabels** - the labels for the filters.
 * * **jobBasicInfo** - the basic info for the job (for applicants list); optional.
 */
export const TopBarConfineResults = (props: CustomPillTopBarProps) => {
  // Destructure props
  const {
    submitFunc,
    recordFunc,
    clearGroup,
    idPrefix,
    queryParams,
    updateFormElements,
    jobBasicInfo,
    formId,
  } = props;


  // Filters labels
  const filtersLabels = useMemo(() => ({
    searchInApplicants: 'global.filter.only-my-applicants',
    searchInSavedApplicants: 'global.filter.only-saved-applicants',
    driversLicence: 'global.filter.with-drivers-license',
    recommendedByAgent: 'resume.filter.label.recommendation',
    excludeContacted: 'global.filter.exclude-contacted',
    passedThroughTheInterview: 'resume.filter.label.with_interview',
    videoInterviewOnly: 'global.filter.only-video-interview',
  }), []);


  // Render the panel
  // **********************************************
  const renderPanel = () => (
    <ConfineResultsForm
      recordFunc={recordFunc}
      submitFunc={submitFunc}
      idPrefix={idPrefix}
      verticalSpacing="gap-0 divide-y divide-dashed"
      customLabelStyles={`${filterStyles.listboxOption} !m-0 !py-2.5`}
      jobBasicInfo={jobBasicInfo}
      formId={formId}
    />
  );


  // Render component
  // **********************************************
  return (
    <TopBarMixedGroup
      submitFunc={submitFunc}
      clearGroup={clearGroup}
      updateFormElements={updateFormElements}
      buttonLabel="filters.groups.confine.title"
      filtersLabels={filtersLabels}
      renderPanel={renderPanel}
      queryParams={queryParams}
    />
  );
};
